import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Package,
  Clock,
  CheckCircle,
  Truck,
  Eye,
  Download,
  Search,
  Filter
} from 'lucide-react';
import { formatPrice, formatDate, getStatusColor } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';

export default function OrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to view your orders');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API_URL}/api/orders/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = !searchQuery || 
      order.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.items?.some(item => item.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const orderStatus = order.order_status || order.status || 'pending';
    const matchesTab = activeTab === 'all' || orderStatus === activeTab;
    
    return matchesSearch && matchesTab;
  });

  const getOrderStatusIcon = (status) => {
    const statusValue = status || 'pending';
    switch (statusValue) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'processing':
      case 'confirmed':
        return <Package className="w-4 h-4" />;
      case 'packed':
        return <Package className="w-4 h-4" />;
      case 'shipped':
      case 'dispatched':
        return <Truck className="w-4 h-4" />;
      case 'delivered':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const orderCounts = {
    all: orders.length,
    pending: orders.filter(o => (o.order_status || o.status) === 'pending').length,
    processing: orders.filter(o => ['processing', 'confirmed', 'packed'].includes(o.order_status || o.status)).length,
    shipped: orders.filter(o => ['shipped', 'dispatched'].includes(o.order_status || o.status)).length,
    delivered: orders.filter(o => (o.order_status || o.status) === 'delivered').length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
              <p className="text-gray-600 mt-2">
                Track and manage your order history
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="orders-search-input"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {orders.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-12 h-12 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No orders yet</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              You haven't placed any orders yet. Start shopping to see your orders here.
            </p>
            <Link to="/products">
              <Button className="btn-primary" data-testid="start-shopping-button">
                Start Shopping
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Order Status Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
              <TabsList className="grid w-full grid-cols-5 max-w-2xl">
                <TabsTrigger value="all" className="relative">
                  All Orders
                  {orderCounts.all > 0 && (
                    <Badge variant="secondary" className="ml-2">{orderCounts.all}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="pending">
                  Pending
                  {orderCounts.pending > 0 && (
                    <Badge variant="secondary" className="ml-2">{orderCounts.pending}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="processing">
                  Processing
                  {orderCounts.processing > 0 && (
                    <Badge variant="secondary" className="ml-2">{orderCounts.processing}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="shipped">
                  Shipped
                  {orderCounts.shipped > 0 && (
                    <Badge variant="secondary" className="ml-2">{orderCounts.shipped}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="delivered">
                  Delivered
                  {orderCounts.delivered > 0 && (
                    <Badge variant="secondary" className="ml-2">{orderCounts.delivered}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Orders List */}
            <div className="space-y-6">
              {filteredOrders.length === 0 ? (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No orders found</h3>
                  <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
                </div>
              ) : (
                filteredOrders.map((order) => {
                  const orderStatus = order.order_status || order.status || 'pending';
                  const paymentMethod = order.payment_method || 'ONLINE';
                  const paymentStatus = order.payment_status || 'pending';
                  const orderTotal = order.total || order.subtotal || 0;
                  
                  // Format payment status for display
                  const getPaymentDisplay = () => {
                    if (paymentMethod === 'COD') return { label: 'Cash on Delivery', color: 'bg-amber-100 text-amber-800' };
                    if (paymentStatus === 'paid') return { label: 'Paid', color: 'bg-green-100 text-green-800' };
                    if (paymentStatus === 'pending') return { label: 'Payment Pending', color: 'bg-yellow-100 text-yellow-800' };
                    if (paymentStatus === 'cod') return { label: 'Cash on Delivery', color: 'bg-amber-100 text-amber-800' };
                    return { label: paymentStatus, color: 'bg-gray-100 text-gray-800' };
                  };
                  
                  const paymentDisplay = getPaymentDisplay();
                  
                  return (
                  <Card key={order.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-200" data-testid={`order-card-${order.id}`}>
                    <CardHeader className="bg-gray-50 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            {getOrderStatusIcon(orderStatus)}
                            <div>
                              <CardTitle className="text-lg font-semibold">
                                Order #{order.id?.slice(-8)?.toUpperCase()}
                              </CardTitle>
                              <p className="text-sm text-gray-600">
                                Placed on {formatDate(order.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900" data-testid={`order-total-${order.id}`}>
                              {formatPrice(orderTotal)}
                            </p>
                            <Badge 
                              className={`${getStatusColor(orderStatus)} capitalize`}
                              data-testid={`order-status-${order.id}`}
                            >
                              {orderStatus}
                            </Badge>
                          </div>
                          
                          <div className="flex flex-col space-y-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              data-testid={`view-order-${order.id}`}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </Button>
                            {orderStatus === 'delivered' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                data-testid={`download-invoice-${order.id}`}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Invoice
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        {/* Order Items */}
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Order Items</h4>
                          <div className="space-y-3">
                            {order.items?.slice(0, 3).map((item, index) => (
                              <div key={index} className="flex items-center space-x-4">
                                {item.image ? (
                                  <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded-lg flex-shrink-0" />
                                ) : (
                                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex-shrink-0 flex items-center justify-center">
                                    <Package className="w-6 h-6 text-gray-400" />
                                  </div>
                                )}
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">{item.name}</p>
                                  <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                                </div>
                                <p className="font-semibold text-gray-900">
                                  {formatPrice(item.price * item.quantity)}
                                </p>
                              </div>
                            ))}
                            {order.items?.length > 3 && (
                              <p className="text-sm text-gray-500 text-center py-2">
                                + {order.items.length - 3} more items
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Order Summary */}
                        <div className="border-t pt-4">
                          <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center flex-wrap gap-4">
                              {/* Payment Method */}
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">Payment:</span>
                                <Badge className={`${paymentDisplay.color} text-xs`}>
                                  {paymentDisplay.label}
                                </Badge>
                              </div>
                              
                              {/* Order Status */}
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">Status:</span>
                                <Badge className={`${getStatusColor(orderStatus)} capitalize text-xs`}>
                                  {orderStatus}
                                </Badge>
                              </div>
                              
                              {order.tracking_number && (
                                <div className="flex items-center space-x-2">
                                  <Truck className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-600">Tracking:</span>
                                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                                    {order.tracking_number}
                                  </code>
                                </div>
                              )}
                            </div>
                            
                            {orderStatus === 'delivered' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                data-testid={`reorder-${order.id}`}
                              >
                                Reorder
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );})
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
